import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { H3, Btn } from '../../../AbstractElements';
import { addFaseProgetto, getFasiProgetto } from '../../../Features/progetti/api/progettoFasi.api';
import { getCategoriesByParent } from '../../../Features/categorie/api/categorie.api';
import { formatDateIT, parseMetaName } from '../../../Helpers';
import DeleteConfirm from '../../../Components/Modals/Delete';

type FaseOption = string;
type FaseSelectOption = { id: number; label: string };

type FaseRow = {
  id: number;
  faseID: FaseOption;
  data: string;
};

type DraftRow = {
  descrizione: string;
  data: string;
};

const EMPTY_DRAFT: DraftRow = {
  descrizione: '',
  data: '',
};

export default function FasiProgetto() {
  const { id: progettoId } = useParams<{ id: string }>();
  const [rows, setRows] = useState<FaseRow[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [draft, setDraft] = useState<DraftRow>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [descrizioneOptions, setDescrizioneOptions] = useState<FaseSelectOption[]>([]);
  const [selectedDescrizioneWord, setSelectedDescrizioneWord] = useState<string>('');

  const loadFasi = async () => {
    if (!progettoId) return;

    const progettoID = Number(progettoId);
    if (!Number.isFinite(progettoID)) return;

    try {
      setLoadingRows(true);
      const list = await getFasiProgetto(progettoID);
      const mapped: FaseRow[] = list.map((item, index) => ({
        id: Number(item.ID ?? Date.now() + index),
        faseID: String(item.faseID ?? item.descrizione ?? ''),
        data: String(item.data ?? ''),
      }));
      setRows(mapped);
    } catch (err) {
      console.error('Errore caricando le fasi progetto:', err);
      toast.error('Impossibile caricare le fasi del progetto');
    } finally {
      setLoadingRows(false);
    }
  };

  useEffect(() => {
    const loadCategorie = async () => {
      try {
        const categories = await getCategoriesByParent(686);
        const options = categories
          .filter((c) => (c.abilitato ?? 1) === 1)
          .sort((a, b) => (a.ordine ?? 0) - (b.ordine ?? 0))
          .map((c) => ({
            id: Number(c.ID),
            label: parseMetaName(c.meta, 'it') || c.nome || c.codice || String(c.ID),
          }))
          .filter((item) => !!item.label);
        setDescrizioneOptions(options);
      } catch (err) {
        console.error('Errore caricando le categorie fase:', err);
        toast.error('Impossibile caricare le categorie delle fasi');
      }
    };

    loadCategorie();
  }, []);

  useEffect(() => {
    loadFasi();
  }, [progettoId]);

  const handleAddRow = () => {
    setIsAdding(true);
  };

  const getFaseLabel = (id: string) => {
    const found = descrizioneOptions.find((opt) => String(opt.id) === String(id));
    return found?.label ?? id;
  };

  const handleCancelAdd = () => {
    setDraft(EMPTY_DRAFT);
    setSelectedDescrizioneWord('');
    setIsAdding(false);
  };

  const handleSaveRow = async () => {
    if (!draft.descrizione.trim() || !draft.data) {
      return;
    }

    if (!progettoId) {
      toast.error('Nessun progetto selezionato.');
      return;
    }

    const progettoID = Number(progettoId);
    if (!Number.isFinite(progettoID)) {
      toast.error('ID progetto non valido.');
      return;
    }

    try {
      setSaving(true);
      const res = await addFaseProgetto({
        progettoID,
        descrizione: draft.descrizione.trim(),
        data: draft.data,
      });

      if (res?.status && String(res.status).toLowerCase() !== 'ok') {
        toast.error(res.message ?? 'Errore durante il salvataggio della fase.');
        return;
      }

      setDraft(EMPTY_DRAFT);
      setSelectedDescrizioneWord('');
      setIsAdding(false);
      await loadFasi();
      toast.success(res?.message ?? 'Fase salvata con successo.');
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Errore di rete';
      toast.error(`Errore salvataggio fase: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <H3>Fasi del progetto</H3>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleAddRow}
          disabled={isAdding || saving}
        >
          Aggiungi rigo
        </button>
      </div>

      <div className="card-body">
        {isAdding && (
          <div className="border rounded p-3 mb-3">
            <div className="row g-3 align-items-start">
              <div className="col-md-6">
                <label className="form-label">Descrizione</label>
                <input
                  type="text"
                  className="form-control mb-2"
                  value={draft.descrizione}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      descrizione: e.target.value,
                    }))
                  }
                  placeholder="Scrivi la descrizione..."
                  disabled={saving}
                />
                <select
                  className="form-select"
                  value={selectedDescrizioneWord}
                  onChange={(e) => {
                    const optionId = e.target.value;
                    setSelectedDescrizioneWord(optionId);
                    const selected = descrizioneOptions.find((opt) => String(opt.id) === optionId);
                    setDraft((prev) => ({
                      ...prev,
                      descrizione: selected?.label ?? '',
                    }));
                    setSelectedDescrizioneWord('');
                  }}
                  disabled={saving}
                >
                  <option value="">Aggiungi parola da elenco</option>
                  {descrizioneOptions.map((option) => (
                    <option key={option.id} value={String(option.id)}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label">Data</label>
                <input
                  type="date"
                  className="form-control"
                  value={draft.data}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, data: e.target.value }))
                  }
                />
              </div>

              <div style={{paddingTop:'30px'}} className="col-md-2 d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-light w-100"
                  onClick={handleCancelAdd}
                  disabled={saving}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  className="btn btn-success w-100"
                  onClick={handleSaveRow}
                  disabled={!draft.descrizione.trim() || !draft.data || saving}
                >
                  {saving ? 'Salvo...' : 'Salva'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th style={{width:'50%'}} >Fase</th>
                <th>Data</th>
                <th className="text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-muted">
                    {loadingRows ? 'Caricamento fasi...' : 'Nessuna fase inserita.'}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>{getFaseLabel(row.faseID)}</td>
                    <td>{row.data ? formatDateIT(row.data, false) : ''}</td>
                    <td className="text-end">
                      <DeleteConfirm
                        url={`/progetti/fase?ID=${row.id}`}
                        title="Elimina fase"
                        message="Vuoi eliminare questa fase?"
                        onSuccess={() => {
                          setRows((prev) => prev.filter((item) => item.id !== row.id));
                        }}
                        trigger={
                          <button type="button" className="btn btn-sm btn-outline-danger">
                            <i className="fa-solid fa-trash" />
                          </button>
                        }
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
